import { CheckCircle, Circle } from 'lucide-react'

function hasMeaningfulValue(value) {
  if (value == null) return false
  if (typeof value === 'string') return value.trim().length > 0
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'object') return Object.keys(value).length > 0
  return true
}

const REQUIRED_ITEMS = [
  { key: 'roleTitle', label: 'Your role', hint: 'Job title or function' },
  { key: 'teamContext', label: 'Team info', hint: 'Who you work with' },
  { key: 'topChallenges', label: 'Top challenges', hint: 'Meeting problems to solve' },
  { key: 'desiredOutcomes', label: 'Meeting goals', hint: 'What outcomes matter' },
]

const OPTIONAL_ITEMS = [
  { key: 'meetingPainPoints', label: 'Pain points', hint: 'Specific frustrations' },
  { key: 'operatingPrinciples', label: 'Principles', hint: 'How meetings should run' },
  { key: 'interventionPreferences', label: 'Intervention style', hint: 'How Rithmo steps in' },
  { key: 'directnessPreference', label: 'Directness', hint: 'Direct, balanced, or subtle' },
]

export default function SetupProgressPanel({ extractedProfile = {}, isCompleted = false }) {
  const requiredFilled = REQUIRED_ITEMS.filter((item) => hasMeaningfulValue(extractedProfile[item.key])).length
  const progressPct = Math.round((requiredFilled / REQUIRED_ITEMS.length) * 100)

  if (isCompleted) {
    return (
      <div className="p-4">
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-center">
          <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
          <p className="text-sm font-semibold text-emerald-800">Setup complete</p>
          <p className="text-xs text-emerald-600 mt-1">Your preferences are saved</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 space-y-4">
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Setup progress</p>
          <span className="text-xs font-medium text-gray-400">{requiredFilled}/{REQUIRED_ITEMS.length}</span>
        </div>
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-primary-500 to-cyan-500 rounded-full transition-all duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Required</p>
        <div className="space-y-1.5">
          {REQUIRED_ITEMS.map((item) => {
            const captured = hasMeaningfulValue(extractedProfile[item.key])
            return (
              <div key={item.key} className="flex items-start gap-2">
                {captured ? (
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                ) : (
                  <Circle className="w-4 h-4 text-gray-300 mt-0.5 flex-shrink-0" />
                )}
                <div className="min-w-0">
                  <p className={`text-xs font-medium ${captured ? 'text-gray-700' : 'text-gray-400'}`}>{item.label}</p>
                  {!captured && <p className="text-[10px] text-gray-300">{item.hint}</p>}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Optional</p>
        <div className="space-y-1.5">
          {OPTIONAL_ITEMS.map((item) => {
            const captured = hasMeaningfulValue(extractedProfile[item.key])
            return (
              <div key={item.key} className="flex items-start gap-2">
                {captured ? (
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                ) : (
                  <Circle className="w-4 h-4 text-gray-200 mt-0.5 flex-shrink-0" />
                )}
                <div className="min-w-0">
                  <p className={`text-xs font-medium ${captured ? 'text-gray-700' : 'text-gray-300'}`}>{item.label}</p>
                  {!captured && <p className="text-[10px] text-gray-200">{item.hint}</p>}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
